import { FormErrors } from './types';
import { UserFormData } from '../../pages/user-form-types';
import { RoleFormData } from '../../pages/user-form-types';

// Email validation regex - Standard RFC 5322 compliant regex without domain restrictions
export const emailRegexStrict = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

// Phone number validation regex
export const phoneRegex = /^(\+?1)?[-. ]?\(?(\d{3})\)?[-. ]?(\d{3})[-. ]?(\d{4})$/;

// Check if a local is valid (not N/A)
export const isValidLocal = (local: any): boolean => {
    return local && local.unionNumber && String(local.unionNumber).toUpperCase() !== 'N/A';
};

// Check if a province has valid locals
export const hasValidLocals = (locals: any[]): boolean => {
    return locals.some(local => isValidLocal(local));
};

// Check if locals are required for a province (has valid locals available)
export const areLocalsRequired = (filteredLocals: any[]): boolean => {
    return hasValidLocals(filteredLocals);
};

export const validateForm = (formData: UserFormData, roles: RoleFormData[], filteredLocals: any[] = []): { isValid: boolean; errors: FormErrors } => {
    const errors: FormErrors = {
        name: '',
        email: '',
        mobileNumber: '',
        role: '',
        region: '',
        local: ''
    };
    let isValid = true;

    // Name validation
    if (!formData.name.trim()) {
        errors.name = 'Name is required';
        isValid = false;
    } else if (formData.name.trim().length < 2) {
        errors.name = 'Name must be at least 2 characters';
        isValid = false;
    } else if (formData.name.trim().length > 50) {
        errors.name = 'Name must not exceed 50 characters';
        isValid = false;
    } else if (!/^[a-zA-Z\s'-]*$/.test(formData.name.trim())) {
        errors.name = 'Name should only contain letters, spaces, apostrophes, and hyphens';
        isValid = false;
    }

    // Email validation
    if (!formData.email.trim()) {
        errors.email = 'Email is required';
        isValid = false;
    } else {
        if (!emailRegexStrict.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
            isValid = false;
        }
    }

    // Mobile number validation
    if (!formData.mobileNumber) {
        errors.mobileNumber = 'Mobile number is required';
        isValid = false;
    } else {
        if (!phoneRegex.test(formData.mobileNumber)) {
            errors.mobileNumber = 'Please enter a valid Canadian phone number';
            isValid = false;
        }
    }

    // Role validation
    if (!formData.role || formData.role === '0' || formData.role === '4') {
        errors.role = 'Please select a role';
        isValid = false;
    }

    // Region and Local validation (only if role is organizer)
    const selectedRole = roles.find(role => role.id === formData.role);
    if (selectedRole?.name === 'organizer') {
        if (!formData.region || formData.region.length === 0 || formData.region.toString().trim() === '') {
            errors.region = 'Please select a province';
            isValid = false;
        }

        // Only require locals if the province has valid locals available
        if (areLocalsRequired(filteredLocals)) {
            if (!formData.local || formData.local.length === 0) {
                errors.local = 'Please select at least one local area';
                isValid = false;
            }
        }
    }

    return { isValid, errors };
};

export const formatPhoneNumber = (value: string): string => {
    // Remove all non-numeric characters
    let cleaned = value.replace(/\D/g, '');

    // Ensure the number starts with 1 if it's 11 digits
    if (cleaned.length === 11 && cleaned.charAt(0) === '1') {
        cleaned = cleaned.substring(1);
    }

    // Format the number as (XXX) XXX-XXXX
    let formatted = cleaned;
    if (cleaned.length > 0) {
        if (cleaned.length <= 3) {
            formatted = `(${cleaned}`;
        } else if (cleaned.length <= 6) {
            formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
        } else {
            formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
        }
    }

    return formatted;
};

export const getDefaultFormData = (): UserFormData => {
    return {
        id: '',
        name: '',
        email: '',
        mobileNumber: '',
        status: 'active',
        role: '',
        region: [''],
        local: []
    };
};

export const hasFormData = (formData: UserFormData): boolean => {
    return formData.name.trim() !== '' ||
        formData.email.trim() !== '' ||
        formData.mobileNumber.trim() !== '' ||
        formData.role !== '' ||
        (formData.region && formData.region.length > 0)||
        (formData.local != null && formData.local.length > 0)
};
